import { useState } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { ChevronRight, MoreHorizontal, Plus, Trash2, Zap, GripVertical } from 'lucide-react'
import type { RequirementNode, RequirementType, RequirementPriorityLevel, RequirementStatus, RequirementTheme } from '@ispot/shared'
import {
  REQUIREMENT_TYPE_LABELS,
  REQUIREMENT_PRIORITY_LABELS,
  REQUIREMENT_STATUS_LABELS,
  getAllowedRequirementTypesAtDepth,
} from './constants'
import { InlineTextEdit, InlineSelect } from './InlineEditors'
import {
  PersonaPortrait,
  KindBadge,
  PriorityCell,
  StatusCell,
  EvidenceBar,
  AvatarStack,
  KIND_TOKENS,
  personaGradient,
} from './cells'
import { gridTemplate } from './grid'
import type { PersonaLookupEntry } from './usePersonaLookup'
import { ThemeChips } from './ThemeChips'
import { CompletenessCell } from './CompletenessCell'

interface RequirementRowProps {
  node: RequirementNode
  themeById?: Map<string, RequirementTheme>
  visibleColumns: Record<string, boolean>
  isExpanded: boolean
  isSelected: boolean
  isDetailOpen: boolean
  onToggleExpand: (id: string) => void
  onToggleSelect: (id: string) => void
  onUpdate: (id: string, data: Partial<RequirementNode>) => Promise<unknown>
  onDelete: (id: string) => Promise<void>
  onAddChild: (parentId: string) => void
  onGenerateChildren: (parentId: string) => void
  onToggleDetail: (id: string) => void
  isDraggable?: boolean
  density?: 'compact' | 'comfortable'
  resolvePersona?: (name: string | undefined) => PersonaLookupEntry | null
}

function countDescendants(node: RequirementNode): number {
  return node.children.reduce((sum, c) => sum + 1 + countDescendants(c), 0)
}

type EditField = 'name' | 'description' | 'type' | 'priority' | 'status' | null

export function RequirementRow({
  node, themeById, visibleColumns, isExpanded, isSelected, isDetailOpen,
  onToggleExpand, onToggleSelect, onUpdate, onDelete, onAddChild, onGenerateChildren,
  onToggleDetail,
  isDraggable = false,
  density = 'comfortable',
  resolvePersona,
}: RequirementRowProps) {
  const [showMenu, setShowMenu] = useState(false)
  const [editing, setEditing] = useState<EditField>(null)
  const hasChildren = node.children.length > 0
  const indent = node.depth * 20
  const allowedTypeOptions = Object.fromEntries(
    getAllowedRequirementTypesAtDepth(node.depth).map(type => [type, REQUIREMENT_TYPE_LABELS[type]]),
  ) as Record<RequirementType, string>
  const canEditType = Object.keys(allowedTypeOptions).length > 1
  const storedPersona = node.metadata?.linkedPersona
  const resolved = resolvePersona?.(storedPersona?.name)
  // Enrich persisted persona with live workshop data (photo/role) when we have a match
  const linkedPersona = storedPersona
    ? {
        ...storedPersona,
        photo: storedPersona.photo || resolved?.photo,
        role: storedPersona.role || resolved?.role || '',
      }
    : undefined
  const isStory = node.type === 'story'
  const hasLinkedPersona = isStory && !!linkedPersona

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: node.id, disabled: !isDraggable })

  const dragStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    zIndex: isDragging ? 10 : undefined,
  }

  const rowHeight = density === 'compact' ? 34 : 44
  const rowBg = isDetailOpen
    ? 'color-mix(in oklab, var(--color-accent) 8%, var(--color-surface))'
    : isSelected
      ? 'color-mix(in oklab, var(--color-accent) 6%, var(--color-surface))'
      : undefined

  function saveField(field: string, value: unknown) {
    setEditing(null)
    onUpdate(node.id, { [field]: value || null })
  }

  return (
    <div
      ref={setNodeRef}
      className="group relative"
      role="row"
      style={{
        ...dragStyle,
        display: 'grid',
        gridTemplateColumns: gridTemplate(visibleColumns),
        alignItems: 'center',
        minHeight: rowHeight,
        borderBottom: '1px solid var(--color-border-subtle)',
        background: rowBg,
        transition: `background 120ms cubic-bezier(0.2,0,0,1)${transition ? `, ${transition}` : ''}`,
        cursor: 'pointer',
      }}
      onClick={() => onToggleDetail(node.id)}
      onMouseEnter={e => { if (!rowBg) e.currentTarget.style.background = 'var(--color-warm-100)' }}
      onMouseLeave={e => { if (!rowBg) e.currentTarget.style.background = '' }}
    >
      {/* checkbox */}
      <div
        className="flex items-center justify-center"
        style={{ paddingLeft: 8 }}
        onClick={(e) => { e.stopPropagation(); onToggleSelect(node.id) }}
      >
        <div
          className="flex items-center justify-center cursor-pointer"
          style={{
            width: 14, height: 14, borderRadius: 3,
            border: `1.5px solid ${isSelected ? 'var(--color-accent)' : 'var(--color-warm-400)'}`,
            background: isSelected ? 'var(--color-accent)' : 'transparent',
            transition: 'all 120ms cubic-bezier(0.2,0,0,1)',
          }}
        >
          {isSelected && (
            <svg width="9" height="7" viewBox="0 0 10 8" fill="none" aria-hidden>
              <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
      </div>

      {/* expand caret */}
      <div
        className="flex items-center justify-center"
        onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggleExpand(node.id) }}
      >
        {hasChildren ? (
          <ChevronRight
            className="cursor-pointer"
            style={{
              width: 14, height: 14,
              color: 'var(--color-text-muted)',
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 180ms cubic-bezier(0.2,0,0,1)',
            }}
          />
        ) : null}
      </div>

      {/* name column */}
      <div className="flex items-center gap-2 min-w-0 relative" style={{ paddingLeft: indent + 4, paddingRight: 8 }}>
        {/* indent guide for children */}
        {node.depth > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: indent - 12,
              top: 0,
              bottom: 0,
              width: 1,
              background: 'var(--color-border-subtle)',
            }}
          />
        )}

        {/* drag handle — hover only */}
        {isDraggable && (
          <div
            className="opacity-0 group-hover:opacity-100 cursor-grab flex-shrink-0"
            style={{ color: 'var(--color-warm-400)', transition: 'opacity 120ms' }}
            {...attributes}
            {...listeners}
            onClick={e => e.stopPropagation()}
          >
            <GripVertical style={{ width: 12, height: 12 }} />
          </div>
        )}

        {/* kind badge — always present, regardless of linked persona */}
        <div className="flex-shrink-0">
          <KindBadge type={node.type} size={24} />
        </div>

        {/* title + kind/persona label stack */}
        <div className="flex flex-col min-w-0" style={{ gap: 1 }}>
          {editing === 'name' ? (
            <InlineTextEdit
              value={node.name}
              bold={node.depth === 0}
              onSave={v => saveField('name', v)}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div className="flex items-center gap-1.5 min-w-0">
              <span
                className="truncate"
                style={{
                  fontSize: 13,
                  fontWeight: 550,
                  color: 'var(--color-text)',
                  letterSpacing: '-0.005em',
                }}
                onDoubleClick={(e) => { e.stopPropagation(); setEditing('name') }}
              >
                {node.name}
              </span>
              {hasChildren && (
                <span
                  className="flex-shrink-0 font-mono tabular-nums"
                  style={{
                    fontSize: 10,
                    fontWeight: 500,
                    padding: '1px 6px',
                    borderRadius: 999,
                    background: 'var(--color-warm-100)',
                    color: 'var(--color-text-muted)',
                    lineHeight: 1.4,
                  }}
                >
                  {countDescendants(node)}
                </span>
              )}
            </div>
          )}
          {/* subtitle: kind label, optionally followed by linked persona */}
          <div
            className="truncate inline-flex items-center gap-1.5 min-w-0"
            style={{
              fontSize: 10.5,
              fontWeight: 500,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: KIND_TOKENS[node.type].ink,
            }}
          >
            <span className="flex-shrink-0">{REQUIREMENT_TYPE_LABELS[node.type]}</span>
            {hasLinkedPersona && (
              <>
                <span className="flex-shrink-0" style={{ color: 'var(--color-fg-muted)' }}>·</span>
                <PersonaPortrait name={linkedPersona!.name} photo={linkedPersona!.photo} size={14} />
                <span
                  className="truncate"
                  style={{ color: personaGradient(linkedPersona!.name).c2 }}
                >
                  {linkedPersona!.name}{linkedPersona!.role ? ` · ${linkedPersona!.role}` : ''}
                </span>
              </>
            )}
          </div>
        </div>

        {/* hover actions — absolute right of name cell */}
        <div
          className="ml-auto flex items-center gap-0.5 opacity-0 group-hover:opacity-100 flex-shrink-0"
          style={{ transition: 'opacity 120ms' }}
          onClick={e => e.stopPropagation()}
        >
          {node.depth < 3 && (
            <button
              className="flex items-center justify-center rounded cursor-pointer border-0"
              style={{ width: 24, height: 24, background: 'transparent', color: 'var(--color-text-muted)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-warm-200)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              title="Add sub-item"
              onClick={() => onAddChild(node.id)}
            >
              <Plus style={{ width: 16, height: 16 }} />
            </button>
          )}
          <div className="relative">
            <button
              className="flex items-center justify-center rounded cursor-pointer border-0"
              style={{ width: 24, height: 24, background: 'transparent', color: 'var(--color-text-muted)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-warm-200)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              onClick={() => setShowMenu(!showMenu)}
            >
              <MoreHorizontal style={{ width: 16, height: 16 }} />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                <div
                  className="absolute right-0 top-full mt-1 z-50 py-1 rounded-md min-w-[140px]"
                  style={{
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    boxShadow: '0 4px 12px rgba(29,26,21,0.06), 0 1px 3px rgba(29,26,21,0.04)',
                  }}
                >
                  {node.depth < 3 && (
                    <button
                      className="flex items-center gap-2 w-full px-3 py-1.5 text-[13px] text-left hover:bg-[var(--color-warm-50)]"
                      style={{ color: 'var(--color-accent)' }}
                      onClick={() => { setShowMenu(false); onGenerateChildren(node.id) }}
                    >
                      <Zap style={{ width: 16, height: 16 }} /> Generate children
                    </button>
                  )}
                  <button
                    className="flex items-center gap-2 w-full px-3 py-1.5 text-[13px] text-left hover:bg-[var(--color-warm-50)]"
                    style={{ color: 'var(--color-error)' }}
                    onClick={() => { setShowMenu(false); onDelete(node.id) }}
                  >
                    <Trash2 style={{ width: 16, height: 16 }} /> Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* description */}
      {visibleColumns.description !== false && (
        <div
          className="min-w-0"
          style={{ paddingRight: 12 }}
          onClick={e => { e.stopPropagation(); setEditing('description') }}
        >
          {editing === 'description' ? (
            <InlineTextEdit
              value={node.description || ''}
              onSave={v => saveField('description', v)}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <span
              className="truncate block cursor-text"
              style={{ fontSize: 12.5, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}
            >
              {node.description || <span style={{ color: 'var(--color-warm-300)' }}>-</span>}
            </span>
          )}
        </div>
      )}

      {/* themes */}
      {visibleColumns.themes !== false && (
        <div style={{ paddingRight: 12, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
          <ThemeChips node={node} themeById={themeById} />
        </div>
      )}

      {/* priority */}
      {visibleColumns.priority !== false && (
        <div onClick={e => { e.stopPropagation(); setEditing('priority') }}>
          {editing === 'priority' ? (
            <InlineSelect<RequirementPriorityLevel>
              value={node.priority ?? 'medium'}
              options={REQUIREMENT_PRIORITY_LABELS}
              onSave={v => saveField('priority', v)}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <PriorityCell priority={node.priority} />
          )}
        </div>
      )}

      {/* status */}
      {visibleColumns.status !== false && (
        <div onClick={e => { e.stopPropagation(); setEditing('status') }}>
          {editing === 'status' ? (
            <InlineSelect<RequirementStatus>
              value={node.status}
              options={REQUIREMENT_STATUS_LABELS}
              onSave={v => saveField('status', v)}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <StatusCell status={node.status} />
          )}
        </div>
      )}

      {/* assignees */}
      {visibleColumns.assignee !== false && (
        <div>
          {node.assigneeName ? (
            <AvatarStack names={[node.assigneeName]} size={22} />
          ) : null}
        </div>
      )}

      {/* evidence */}
      {visibleColumns.evidence !== false && (
        <div style={{ paddingRight: 14 }}>
          {node.evidence.length > 0
            ? <EvidenceBar count={node.evidence.length} />
            : <span style={{ fontSize: 12, color: 'var(--color-warm-300)' }}>-</span>
          }
        </div>
      )}

      {/* template completeness */}
      {visibleColumns.completeness !== false && (
        <div style={{ paddingRight: 14 }}>
          <CompletenessCell completeness={node.completeness} />
        </div>
      )}
    </div>
  )
}
